import { useQuery } from '@apollo/client';
import { useResource } from '@landsbankinn/l16n';
import { LandsbankinnContext } from '@landsbankinn/utils';
import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import React, { useContext } from 'react';
import type {
  fetchBondsIntradayHistory,
  fetchBondsIntradayHistory_intradayHistory,
} from '../../../../graphql/__generated__/fetchBondsIntradayHistory';
import { FETCH_BONDS_INTRADAY_HISTORY } from '../../../../graphql/fetchBondsIntradayHistory';
import { DataTable } from '../../../table/DataTable';
import {
  MarketsBodyBold,
  StickyFirstColumn,
} from '../../shared/MarketsComponents';
import {
  MarketsFormatNumber,
  NumberType,
} from '../../shared/MarketsFormatNumber';
import res from '../Bonds.res';

interface IProps {
  intraday?: fetchBondsIntradayHistory;
}

export function BondsTradesTable({ intraday }: IProps) {
  const { r } = useResource();
  const { client } = useContext(LandsbankinnContext);
  const { data = intraday } = useQuery<fetchBondsIntradayHistory>(
    FETCH_BONDS_INTRADAY_HISTORY,
    {
      client,
      variables: { first: 100 },
      pollInterval: 30000,
    },
  );

  const columns = React.useMemo(
    () => [
      {
        Header: () => r(res.bond),
        accessor: 'symbol', // accessor is the "key" in the data
        headerStyle: {
          flexDirection: 'row-reverse',
        },
        Cell: ({ value }: any) => <MarketsBodyBold>{value}</MarketsBodyBold>,
      },
      {
        Header: () => r(res.price),
        accessor: 'price',
        Cell: ({ value }: any) => (
          <MarketsBodyBold>
            <MarketsFormatNumber
              type={NumberType.Number}
              decimalScale={2}
              number={value}
            />
          </MarketsBodyBold>
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.nominalValue),
        accessor: 'volume',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={0}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: () => r(res.value),
        accessor: 'value',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={0}
            displayISK={true}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
          whiteSpace: 'nowrap',
        },
      },
      {
        Header: () => r(res.time),
        accessor: 'date',
        Cell: ({ value }: any) =>
          isToday(new Date(value))
            ? format(new Date(value), 'HH:mm')
            : format(new Date(value), 'dd.MM.yyyy HH:mm'),
        style: {
          textAlign: 'right',
        },
      },
    ],
    [],
  );

  const tableData = React.useMemo(() => {
    return data?.intradayHistory;
  }, [data]);

  return (
    <StickyFirstColumn>
      <DataTable<fetchBondsIntradayHistory_intradayHistory>
        aria-label={r(res.bonds)}
        data={tableData ?? []}
        columns={columns}
        colorTheme="primary"
        initialState={{
          sortBy: [
            {
              id: 'date',
              desc: true,
            },
          ],
        }}
      />
    </StickyFirstColumn>
  );
}
